/**
 * Web platform implementation
 *
 * Vite resolves @/platform → this file when TAURI_ENV_PLATFORM is NOT set.
 * Uses axios/fetch for HTTP, localStorage for storage, browser APIs for the rest.
 */
import Axios from "axios";
import { installSessionInterceptors } from "@/context/auth/sessionAxios";
import {
  clearSession,
  prepareSession,
  type SessionTransport,
} from "@/context/auth/sessionRenewal";
import { backendErrorText } from "@/utils/errorMapping";
import { NavigateFunction } from "react-router";
import type {
  PlatformStore,
  PlatformNotifications,
  PlatformUpdater,
  PlatformAppInfo,
  PlatformPdf,
  PlatformCsv,
  PlatformExport,
  LogoutFunction,
  PlatformPrinter,
  PlatformFiles,
  SaveBinaryFileResult,
  PlatformLinks,
  PlatformOAuth,
  PlatformPos,
} from "./types";
import { posApagada } from "./posApagada";
import type { PedirLosFlags } from "./cajaOffline";

// Re-export types for consumers
export type { PlatformStore } from "./types";

// ─── Store (localStorage → sessionStorage → cookies) ────────────────────────

class WebStore implements PlatformStore {
  private isLocalStorageAvailable(): boolean {
    try {
      const test = "__localStorage_test__";
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch {
      return false;
    }
  }

  private isSessionStorageAvailable(): boolean {
    try {
      const test = "__sessionStorage_test__";
      sessionStorage.setItem(test, test);
      sessionStorage.removeItem(test);
      return true;
    } catch {
      return false;
    }
  }

  private setCookie(name: string, value: string, days: number = 365): void {
    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie = `${name}=${encodeURIComponent(
      value
    )};expires=${expires.toUTCString()};path=/;SameSite=Strict`;
  }

  private getCookie(name: string): string | null {
    const nameEQ = name + "=";
    const ca = document.cookie.split(";");
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === " ") c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) {
        return decodeURIComponent(c.substring(nameEQ.length, c.length));
      }
    }
    return null;
  }

  private deleteCookie(name: string): void {
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
  }

  async get(key: string): Promise<string | null> {
    try {
      if (this.isLocalStorageAvailable()) {
        const value = localStorage.getItem(key);
        if (value !== null) return value;
      }
      if (this.isSessionStorageAvailable()) {
        const value = sessionStorage.getItem(key);
        if (value !== null) return value;
      }
      return this.getCookie(key);
    } catch (error) {
      console.warn(`Error getting ${key} from storage:`, error);
      return null;
    }
  }

  async set(key: string, value: string): Promise<void> {
    try {
      if (this.isLocalStorageAvailable()) {
        localStorage.setItem(key, value);
        return;
      }
      if (this.isSessionStorageAvailable()) {
        sessionStorage.setItem(key, value);
        return;
      }
      const isToken = key.includes("token") || key.includes("Token");
      this.setCookie(key, value, isToken ? 7 : 365);
    } catch (error) {
      console.error(`Error setting ${key} in storage:`, error);
      throw error;
    }
  }

  async delete(key: string): Promise<void> {
    try {
      if (this.isLocalStorageAvailable()) localStorage.removeItem(key);
      if (this.isSessionStorageAvailable()) sessionStorage.removeItem(key);
      this.deleteCookie(key);
    } catch (error) {
      console.warn(`Error deleting ${key} from storage:`, error);
    }
  }

  async clear(): Promise<void> {
    try {
      if (this.isLocalStorageAvailable()) localStorage.clear();
      if (this.isSessionStorageAvailable()) sessionStorage.clear();
    } catch (error) {
      console.warn("Error clearing storage:", error);
    }
  }

  async has(key: string): Promise<boolean> {
    return (await this.get(key)) !== null;
  }

  async keys(): Promise<string[]> {
    const keys: string[] = [];
    try {
      if (this.isLocalStorageAvailable()) {
        keys.push(...Object.keys(localStorage));
      }
      if (this.isSessionStorageAvailable()) {
        Object.keys(sessionStorage).forEach((key) => {
          if (!keys.includes(key)) keys.push(key);
        });
      }
    } catch (error) {
      console.warn("Error getting storage keys:", error);
    }
    return keys;
  }
}

const webStore = new WebStore();

export async function loadStore(): Promise<PlatformStore> {
  return webStore;
}

// ─── Internal HTTP helpers (NOT exported — avoids circular deps with AxiosContext) ─

const httpClient = Axios.create({
  baseURL: import.meta.env.VITE_BFF_URL || "",
  headers: { "Content-Type": "application/json" },
});

// Raw channel: refresh and /me must never recursively invoke renewal.
const sessionHttp = Axios.create({
  baseURL: import.meta.env.VITE_BFF_URL || "",
  timeout: 15000,
});
export const sessionTransport: SessionTransport = {
  loadStore,
  async request(method, url, body, token) {
    const response = await sessionHttp.request({
      method,
      url,
      data: body,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return response.data;
  },
};
installSessionInterceptors(httpClient, sessionTransport);

// The lock + persisted generation also protect refresh/logout between tabs.
if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    if (["token", "user", "authSessionId"].includes(event.key ?? "")) {
      // Transient errors remain retryable on the next request, without logging
      // auth payloads or rejecting an unobserved background promise.
      void prepareSession(sessionTransport).catch(() => undefined);
    }
  });
}

// ─── IPC (GraphQL legacy — routes through BFF) ──────────────────────────────

// Opt-in trace de cada GraphQL request/response. Off por default — encender
// con VITE_DEBUG_GRAPHQL=true en .env.local cuando haya que diagnosticar
// algo. Errores siempre se loguean (catch en executeGraphQLQuery).
const DEBUG_GRAPHQL = import.meta.env.VITE_DEBUG_GRAPHQL === "true";

export async function ipc_invoke(
  method: string,
  params: Record<string, any>,
  navigate?: NavigateFunction | null
): Promise<any> {
  if (method === "execute_graphql_query") {
    return await executeGraphQLQuery(params, navigate);
  }

  throw new Error(
    `IPC method '${method}' not supported in web version. Use IpcRest instead.`
  );
}

async function executeGraphQLQuery(
  params: Record<string, any>,
  navigate?: NavigateFunction | null
): Promise<any> {
  try {
    const store = await loadStore();
    const businessId = await store.get("businessId");

    const {
      params: queryParams,
      environment,
      businessId: paramBusinessId,
      searchQuery = "",
    } = params;

    const finalBusinessId = paramBusinessId || businessId || "";

    const requestBody = {
      businessId: finalBusinessId,
      searchQuery,
      params: {
        queryName: queryParams.queryName,
        tableName: queryParams.tableName,
        fields: queryParams.fields,
        operators: queryParams.operators,
        variables: queryParams.variables || {},
        hasBusiness: queryParams.hasBusiness,
        hasPagination: queryParams.hasPagination,
      },
    };

    const bffEndpoint = "/query-builder";

    if (DEBUG_GRAPHQL) {
      console.log("GraphQL Query Request:", {
        method: "execute_graphql_query",
        endpoint: bffEndpoint,
        businessId: finalBusinessId,
        queryName: queryParams.queryName,
        tableName: queryParams.tableName,
      });
    }

    const response = await httpClient.post(bffEndpoint, requestBody, {
      headers: { "x-env": environment || "development" },
    });

    // BFF wraps Hasura's response: { status_code, data: <hasura-response> }
    // Hasura response shape: { data: { tableName: [...] } } or
    //                        { data: null, errors: [...] } on failure.
    // We must check both layers — the outer BFF envelope AND the inner
    // Hasura `errors` field. The old code only checked the outer envelope,
    // so Hasura permission/query errors silently surfaced as `data: null`
    // to react-query, which caused crashes downstream (see
    // `feedback_optional_chaining_hides_bugs`).
    const responseData = response.data;

    if (responseData.error) {
      throw new Error(responseData.error);
    }

    const hasuraResponse = responseData.data;

    if (
      hasuraResponse?.errors &&
      Array.isArray(hasuraResponse.errors) &&
      hasuraResponse.errors.length > 0
    ) {
      const messages = hasuraResponse.errors
        .map((e: any) => e?.message ?? String(e))
        .join(", ");
      throw new Error(`Hasura error: ${messages}`);
    }

    if (DEBUG_GRAPHQL) {
      console.log("GraphQL Query Success:", {
        queryName: queryParams.queryName,
        hasData: !!hasuraResponse?.data,
      });
    }

    return hasuraResponse;
  } catch (error) {
    console.error("GraphQL Query Error:", error);

    if (error instanceof Error) {
      if (
        error.message.includes("Token de autorización no válido") &&
        navigate
      ) {
        await logout(navigate);
      }
    }

    throw error;
  }
}

// ─── IpcRest (axios-based) ───────────────────────────────────────────────────

export class IpcRest {
  private static async handleError(
    error: any,
    navigate: NavigateFunction | null
  ): Promise<never> {
    // SIEMPRE un string: el BFF devuelve `error` como OBJETO en su formato
    // estructurado, y el `msg.includes(...)` de abajo tiraba un TypeError
    // adentro de este mismo manejador, que se llevaba puestos el `status` y el
    // `response` del error real. Ver `backendErrorText`.
    const msg = backendErrorText(error);

    if (
      (msg.includes("UNAUTHORIZED") ||
        msg.includes("Token de autorización no válido") ||
        error.response?.status === 401) &&
      navigate
    ) {
      await logout(navigate);
    }

    // El mensaje NO alcanza para decidir. Tirar un `Error(msg)` pelado tiraba a
    // la basura el status y el body, así que quien llama no podía distinguir un
    // rechazo del dominio de una caída de red — la misma regla que el CLAUDE.md
    // de este repo ya fija para `reserveItem`.
    //
    // Caso real: identity responde el email duplicado con **409 sin body**
    // (`{"statusCode":409}`). Sin `message` ni `error` en el body, `msg` caía en
    // el genérico de axios y la pantalla de alta mostraba "Error código 3312"
    // sobre un problema que se resuelve cambiando una letra del email.
    //
    // Se preserva `response` (y `status` a mano) sin tocar el `message`, así
    // ningún caller existente cambia de comportamiento: es puramente aditivo.
    const wrapped = new Error(
      typeof msg === "string" ? msg : JSON.stringify(msg)
    ) as Error & { status?: number; response?: unknown };
    wrapped.status = error.response?.status;
    wrapped.response = error.response;
    throw wrapped;
  }

  static async get<T = any>(
    options: {
      url: string;
      params?: Record<string, any>;
      headers?: Record<string, string>;
    },
    navigate: NavigateFunction | null = null
  ): Promise<T> {
    try {
      const res = await httpClient.get(options.url, {
        params: options.params,
        headers: options.headers,
      });
      return res.data;
    } catch (error) {
      return this.handleError(error, navigate);
    }
  }

  static async post<T = any>(
    options: {
      url: string;
      body?: Record<string, any>;
      headers?: Record<string, string>;
    },
    navigate: NavigateFunction | null = null
  ): Promise<T> {
    try {
      const res = await httpClient.post(options.url, options.body, {
        headers: options.headers,
      });
      return res.data;
    } catch (error) {
      return this.handleError(error, navigate);
    }
  }

  static async put<T = any>(
    options: {
      url: string;
      body?: Record<string, any>;
      headers?: Record<string, string>;
    },
    navigate: NavigateFunction | null = null
  ): Promise<T> {
    try {
      const res = await httpClient.put(options.url, options.body, {
        headers: options.headers,
      });
      return res.data;
    } catch (error) {
      return this.handleError(error, navigate);
    }
  }

  static async patch<T = any>(
    options: {
      url: string;
      body?: Record<string, any>;
      headers?: Record<string, string>;
    },
    navigate: NavigateFunction | null = null
  ): Promise<T> {
    try {
      const res = await httpClient.patch(options.url, options.body, {
        headers: options.headers,
      });
      return res.data;
    } catch (error) {
      return this.handleError(error, navigate);
    }
  }

  static async delete<T = any>(
    options: {
      url: string;
      headers?: Record<string, string>;
    },
    navigate: NavigateFunction | null = null
  ): Promise<T> {
    try {
      const res = await httpClient.delete(options.url, {
        headers: options.headers,
      });
      return res.data;
    } catch (error) {
      return this.handleError(error, navigate);
    }
  }
}

// ─── Logout ──────────────────────────────────────────────────────────────────

export const logout: LogoutFunction = async (navigate) => {
  const store = await loadStore();
  await clearSession(store);
  navigate("/login");
};

// ─── Notifications (Browser Notification API) ────────────────────────────────

export const notifications: PlatformNotifications = {
  async sendOSNotification(options) {
    try {
      if (!("Notification" in window)) return;

      if (Notification.permission === "default") {
        await Notification.requestPermission();
      }

      if (Notification.permission === "granted") {
        new Notification(options.title, {
          body: options.body,
          icon: options.icon,
        });
      }
    } catch (error) {
      console.warn("Browser notification failed:", error);
    }
  },
};

// ─── Updater (no-op on web — Vercel handles deploys) ─────────────────────────

export const updater: PlatformUpdater = {
  async checkForUpdate() {
    return null;
  },
  async downloadAndInstall() {
    window.location.reload();
  },
  async relaunch() {
    window.location.reload();
  },
};

// ─── App Info ────────────────────────────────────────────────────────────────

export const appInfo: PlatformAppInfo = {
  async getVersion() {
    return import.meta.env.VITE_APP_VERSION || "web";
  },
};

// ─── PDF (BFF headless-chrome → descarga directa; print como fallback) ───────

export const pdf: PlatformPdf = {
  async generate(htmlContent: string) {
    try {
      // El BFF (`POST /pdf/generate`, headless_chrome) renderiza el HTML a un
      // PDF real y lo devuelve binario → descarga directa, misma fidelidad que
      // el desktop. `httpClient` ya apunta al BFF (VITE_BFF_URL) + inyecta auth.
      const response = await httpClient.post(
        "/pdf/generate",
        { htmlContent },
        { responseType: "blob" }
      );

      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `document-${Date.now()}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch {
      // Fallback: print del navegador si el BFF no está disponible (el usuario
      // puede "Guardar como PDF" desde el cuadro de impresión).
      const printWindow = window.open("", "_blank");
      if (printWindow) {
        printWindow.document.write(htmlContent);
        printWindow.document.close();
        printWindow.print();
      }
    }
  },
};

// ─── CSV (blob download) ────────────────────────────────────────────────────

export const csv: PlatformCsv = {
  async generateAndDownload(command: string, params: Record<string, any>) {
    // Mirror what the desktop Rust client (src-tauri/src/file/csv.rs) sends:
    // a single lowercase `businessid` header, no `X-` prefix. The axios
    // interceptor on this httpClient already injects Authorization + a
    // `businessId` header from the store, but the trd-core inventory/csv
    // endpoint reads specifically `businessid`. Sending both flavors is
    // safer and harmless.
    const response = await httpClient.post(params.url, params.params, {
      responseType: "blob",
      headers: {
        businessid: params.businessId || params.business_id || "",
      },
    });

    const blob = new Blob([response.data], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${command}-${Date.now()}.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  },
};

// ─── Export server-side (POST /export → blob download) ──────────────────────

export const exportApi: PlatformExport = {
  async download({ body, defaultName, mimeType }) {
    try {
      const response = await httpClient.post("/export", body, {
        responseType: "blob",
      });
      const blob = new Blob([response.data], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = defaultName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      return { ok: true };
    } catch (err: any) {
      // Con responseType:"blob", axios entrega el body de error como Blob.
      // Lo leemos como texto/JSON para surfacear el payload (ej. 413).
      const status: number | undefined = err?.response?.status;
      let parsedBody: unknown;
      let message = err instanceof Error ? err.message : String(err);
      const data = err?.response?.data;
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          parsedBody = JSON.parse(text);
          const m = (parsedBody as { message?: string })?.message;
          if (m) message = m;
        } catch {
          /* body no era JSON */
        }
      }
      return {
        ok: false,
        cancelled: false,
        error: message,
        status,
        body: parsedBody,
      };
    }
  },
};

// ─── Printer (no-op on web; thermal ESC/POS is desktop-only) ────────────────

const printerNotAvailable = () =>
  Promise.reject(new Error("Printer not available in web build"));

export const printer: PlatformPrinter = {
  isAvailable: false,
  listSerialPrinters: () => Promise.resolve({ data: [] }),
  listCupsPrinters: () => Promise.resolve(""),
  listWinPrinters: () => Promise.resolve(""),
  printTicket: printerNotAvailable,
  printLabels: printerNotAvailable,
  testPrinter: printerNotAvailable,
};

// ─── Caja offline (sólo escritorio: la web vende online, D5) ────────────────

// La caja de la web es la caja apagada (ver `posApagada.ts`).
export const pos: PlatformPos = posApagada;

// ─── Arranque ───────────────────────────────────────────────────────────────

/**
 * Lo que la plataforma prepara antes de dibujar la primera pantalla. La web no
 * tiene nada: no hay caja offline que prender (D5). El escritorio mira acá el
 * flag `OFFLINE_ENABLED`.
 */
export async function iniciarPlataforma(): Promise<void> {}

/**
 * La web no tiene caja offline (D5): no hay valor que dejar para la próxima vez,
 * así que ni siquiera le pregunta al BFF.
 */
export const refrescarLaCajaOffline: (
  pedir: PedirLosFlags
) => Promise<void> = async () => {};

// ─── Files (HTML5 drag-drop + Blob download) ────────────────────────────────

export const files: PlatformFiles = {
  async onFileDrop({ onFiles, onState }) {
    const onDragOver = (e: DragEvent) => {
      // Required to allow the drop event to fire.
      e.preventDefault();
      onState?.("over");
    };
    const onDragLeave = (e: DragEvent) => {
      e.preventDefault();
      onState?.("leave");
    };
    const onDrop = (e: DragEvent) => {
      e.preventDefault();
      onState?.("leave");
      const dropped: File[] = [];
      if (e.dataTransfer?.files) {
        for (const f of Array.from(e.dataTransfer.files)) dropped.push(f);
      }
      if (dropped.length > 0) onFiles(dropped);
    };
    window.addEventListener("dragover", onDragOver);
    window.addEventListener("dragleave", onDragLeave);
    window.addEventListener("drop", onDrop);
    return () => {
      window.removeEventListener("dragover", onDragOver);
      window.removeEventListener("dragleave", onDragLeave);
      window.removeEventListener("drop", onDrop);
    };
  },
  async saveBinaryFile({
    data,
    defaultName,
    mimeType,
  }): Promise<SaveBinaryFileResult> {
    try {
      // Convert the typed-array view to a fresh ArrayBuffer so the resulting
      // Blob is always backed by plain bytes — works regardless of whether
      // `data.buffer` is a SharedArrayBuffer or a sliced view.
      const ab =
        data.byteOffset === 0 && data.byteLength === data.buffer.byteLength
          ? (data.buffer as ArrayBuffer)
          : data.slice().buffer;
      const blob = new Blob([ab], {
        type: mimeType ?? "application/octet-stream",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = defaultName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      return { ok: true };
    } catch (err) {
      return {
        ok: false,
        cancelled: false,
        error: err instanceof Error ? err.message : String(err),
      };
    }
  },
};

// ─── OAuth (popup + postMessage bridge) ─────────────────────────────────────

const OAUTH_MESSAGE_PREFIX = "trud-oauth:";

export const oauth: PlatformOAuth = {
  async beginAuthFlow(url) {
    // Centered popup keeps the parent integrations page mounted, so the
    // hook's promise can resolve when the popup posts back. Browsers
    // allow window.open during a direct user gesture (button click).
    const w = 700;
    const h = 820;
    const left = Math.max(0, (window.screen.width - w) / 2);
    const top = Math.max(0, (window.screen.height - h) / 2);
    const features = `width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=yes`;
    const popup = window.open(url, "trud-oauth", features);
    if (!popup) {
      throw new Error(
        "El navegador bloqueó la ventana emergente. Permitila para conectar."
      );
    }
  },
  async subscribeToProviderCallback(provider, { onSuccess, onError }) {
    const successType = `${OAUTH_MESSAGE_PREFIX}${provider}-success`;
    const errorType = `${OAUTH_MESSAGE_PREFIX}${provider}-error`;
    const handler = (event: MessageEvent) => {
      // Same-origin only — the OAuth callback page lives in this SPA.
      if (event.origin !== window.location.origin) return;
      const data = event.data as
        | { type?: string; payload?: unknown }
        | undefined;
      if (!data || typeof data.type !== "string") return;
      if (data.type === successType) {
        onSuccess(data.payload as never);
      } else if (data.type === errorType) {
        onError(data.payload as never);
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  },
};

/**
 * Message types posted by the `/integrations/:provider/:status` route to
 * its opener. Exported so the route component uses the exact same wire
 * format as the subscriber above.
 */
export const OAUTH_MESSAGE_TYPES = {
  success: (provider: string) => `${OAUTH_MESSAGE_PREFIX}${provider}-success`,
  error: (provider: string) => `${OAUTH_MESSAGE_PREFIX}${provider}-error`,
};

// ─── Build-time flag ─────────────────────────────────────────────────────────

// ─── Links externos ──────────────────────────────────────────────────────────

export const links: PlatformLinks = {
  async openExternal(url) {
    // `noopener,noreferrer` corta el acceso de la pestaña nueva a
    // `window.opener`. Sin gesto del usuario esto lo bloquea el navegador
    // (ver el comentario del popup de OAuth más arriba), así que los callers
    // lo invocan dentro del onClick.
    window.open(url, "_blank", "noopener,noreferrer");
  },
};

export const isDesktop = false;
